import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";

import { upgradeSubscription } from "../../account/store/actionCreators";
import ConfirmUpgradeDialog from "../../account/ConfirmUpgradeDialog";

/**
 * Global host for the in-place (server-side) upgrade confirmation dialog.
 *
 * Registers `window.showUpgradeConfirm(targetPlan)` — mirroring the existing
 * `window.showPaymentWindow` idiom — so the centralized `openUpgrade()` in
 * useSubscriptionGate can trigger the confirm-then-upgrade flow for clean paid
 * users without threading React context through every hint surface.
 *
 * On confirm it dispatches `upgradeSubscription` (POST /subscription/change ->
 * FastSpring proration) and, on success, reloads the page so every derived limit
 * and badge reflects the new plan.
 *
 * Rendered once from FastspringIntegration, which is already mounted in all
 * authenticated layouts.
 */
const UpgradeDialogHost = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [pendingPlan, setPendingPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  // Registered in the render body (like FastspringIntegration's window.* handlers) so the
  // currently-rendered host always owns the global, even across layout transitions.
  window.showUpgradeConfirm = (targetPlan) => {
    if (!targetPlan) {
      return;
    }
    setLoading(false);
    setPendingPlan(targetPlan);
  };

  if (!pendingPlan) {
    return null;
  }

  const onConfirm = () => {
    setLoading(true);
    dispatch(
      upgradeSubscription(
        intl,
        pendingPlan,
        () => window.location.reload(),
        () => setLoading(false)
      )
    );
  };

  const onCancel = () => {
    if (loading) {
      return;
    }
    setPendingPlan(null);
  };

  return (
    <ConfirmUpgradeDialog
      planKey={pendingPlan}
      onConfirm={onConfirm}
      onCancel={onCancel}
      loading={loading}
    />
  );
};

export default UpgradeDialogHost;
